
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import Header from "./header";
import Footer from "./Footer";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { register } from "../store/slices/auth";
import { getUserDetailsByUserId } from "../store/slices/fund";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LIMITS = {
  name: 50,
  email: 75,
  mobileNo: 10,
  password: 25,
  referralCode: 10,
};

function validate(values) {
  const errors = {};

  if (!values.name.trim()) {
    errors.name = "Please enter your full name.";
  } else if (values.name.length > LIMITS.name) {
    errors.name = `Name must be ${LIMITS.name} characters or fewer.`;
  }

  if (!values.email.trim()) {
    errors.email = "Please enter your email.";
  } else if (!EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = "Please enter a valid email.";
  }

  if (!values.mobileNo.trim()) {
    errors.mobileNo = "Please enter your mobile number.";
  } else if (!/^\d{10}$/.test(values.mobileNo)) {
    errors.mobileNo = "Please enter a valid 10-digit mobile number.";
  }

  if (!values.referralCode.trim()) {
    errors.referralCode = "Please enter your referral code.";
  } else if (values.referralCode.length > LIMITS.referralCode) {
    errors.referralCode =
      `Referral code must be ${LIMITS.referralCode} characters or fewer.`;
  }

  if (!values.password) {
    errors.password = "Please enter a password.";
  } else if (values.password.length < 6) {
    errors.password = "Password must be at least 6 characters.";
  }

  return errors;
}


const INITIAL_VALUES = {
  name: "",
  email: "",
  mobileNo: "",
  password: "",
  referralCode: "",
};

export default function Register() {
  const { referralCode } = useParams();

  const [values, setValues] = useState(INITIAL_VALUES);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [referralUser, setReferralUser] = useState(null);
  const [referralUserLoading, setReferralUserLoading] = useState(false);
  const [referralUserInvalid, setReferralUserInvalid] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const visualRef = useRef(null);

  useEffect(() => {
    if (referralCode) {
      setValues((prev) => ({
        ...prev,
        referralCode: referralCode.toUpperCase(),
      }));
    }
  }, [referralCode]);

  const fetchReferralUser = async (referralId) => {
    const referral = referralId?.trim().toUpperCase();

    if (!referral) {
      setReferralUser(null);
      setReferralUserInvalid(false);
      return;
    }

    try {
      setReferralUserLoading(true);
      setReferralUser(null);
      setReferralUserInvalid(false);

      const response = await dispatch(
        getUserDetailsByUserId({
          userId: referral,
        })
      ).unwrap();

      console.log("Referral user response:", response);

      const user =
        response?.data?.user ||
        response?.data ||
        response?.user ||
        response;

      if (user?.name) {
        setReferralUser(user);
        setReferralUserInvalid(false);
      } else {
        setReferralUser(null);
        setReferralUserInvalid(true);
      }
    } catch (error) {
      console.error("Referral user fetch error:", error);

      setReferralUser(null);
      setReferralUserInvalid(true);
    } finally {
      setReferralUserLoading(false);
    }
  };

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const visual = visualRef.current;
    const blobs = visual
      ? visual.querySelectorAll(":scope > .blob")
      : [];

    if (
      !visual ||
      !blobs.length ||
      reduceMotion ||
      !window.matchMedia("(pointer: fine)").matches
    ) {
      return;
    }

    const onMouseMove = (e) => {
      const rect = visual.getBoundingClientRect();

      const cx = (e.clientX - rect.left) / rect.width - 0.5;
      const cy = (e.clientY - rect.top) / rect.height - 0.5;

      blobs.forEach((blob, i) => {
        const strength = 18 + i * 6;
        blob.style.translate = `${cx * strength}px ${cy * strength} px`;
      });
    };

    visual.addEventListener("mousemove", onMouseMove, {
      passive: true,
    });

    return () =>
      visual.removeEventListener("mousemove", onMouseMove);
  }, []);

  function handleChange(e) {
    const { name, type, value, checked } = e.target;

    const nextValue =
      type === "checkbox" ? checked : value;

    const nextValues = {
      ...values,
      [name]: nextValue,
    };

    setValues(nextValues);

    const nextErrors = validate(nextValues);

    setErrors((prev) => {
      const updatedErrors = { ...prev };

      if (nextErrors[name]) {
        updatedErrors[name] = nextErrors[name];
      } else {
        delete updatedErrors[name];
      }

      return updatedErrors;
    });
  }

  function handleBlur(e) {
    const { name } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const nextErrors = validate(values);

    setErrors((prev) => {
      const updatedErrors = { ...prev };

      if (nextErrors[name]) {
        updatedErrors[name] = nextErrors[name];
      } else {
        delete updatedErrors[name];
      }

      return updatedErrors;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const nextErrors = validate(values);

    setErrors(nextErrors);

    setTouched({
      name: true,
      email: true,
      mobileNo: true,
      password: true,
      referralCode: true,
    });

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        name: values.name.trim(),
        email: values.email.trim(),
        mobileNo: values.mobileNo.trim(),
        password: values.password,
        referralCode: values.referralCode.trim(),
      };

      const response = await dispatch(
        register(payload)
      ).unwrap();

      toast.success(
        response?.message || "Registration successful"
      );

      const registeredUser = response?.user || response?.data || {
        name: payload.name,
        email: payload.email,
        referralCode: payload.referralCode,
      };

      navigate("/welcome", {
        replace: true,
        state: {
          user: registeredUser,
        },
      });

    } catch (error) {
      console.error("Registration error:", error);

      const apiError =
        error ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Registration failed. Please try again.";

      toast.error(apiError);

      setNote({
        text: apiError,
        tone: "error",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <> <Header />


      <main className="auth-shell">
        <div className="auth-visual" ref={visualRef}>
          <div className="blob blob1" aria-hidden="true"></div>
          <div className="blob blob2" aria-hidden="true"></div>

          <div className="auth-visual-copy">
            <h2>
              Start your{" "}
              <span className="accent">
                investment journey today.
              </span>
            </h2>

            <p>
              Join Global Inavation and explore opportunities across
              global markets, investments, and financial growth.
            </p>

            <div className="auth-checklist">
              <div>
                <span className="chk">✓</span>
                Explore global opportunities
              </div>

              <div>
                <span className="chk">✓</span>
                Secure account access
              </div>

              <div>
                <span className="chk">✓</span>
                Start your investment journey
              </div>
            </div>
          </div>
        </div>

        <div className="auth-form-side">


          <div className="auth-form-wrap">
            <h1>Register</h1>
            {/* <h2>Site Under Maintenance.</h2> */}


            <form onSubmit={handleSubmit}>

              <div
                className={`field${errors.name ? " has-error" : ""}`}
              >
                <label htmlFor="name">
                  Name
                </label>

                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter your name"
                  autoComplete="name"
                  maxLength={LIMITS.name}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-invalid={!!errors.name}
                />

                {errors.name && (
                  <span className="field-error">
                    {errors.name}
                  </span>
                )}
              </div>
              <div
                className={`field${errors.mobileNo ? " has-error" : ""}`}
              >
                <label htmlFor="mobileNo">
                  Mobile Number
                </label>

                <input
                  type="tel"
                  id="mobileNo"
                  name="mobileNo"
                  placeholder="Enter 10-digit mobile number"
                  autoComplete="tel"
                  maxLength={LIMITS.mobileNo}
                  value={values.mobileNo}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");

                    handleChange({
                      target: {
                        name: "mobileNo",
                        type: "text",
                        value,
                      },
                    });
                  }}
                  onBlur={handleBlur}
                  aria-invalid={!!errors.mobileNo}
                />

                {errors.mobileNo && (
                  <span className="field-error">
                    {errors.mobileNo}
                  </span>
                )}
              </div>

              <div
                className={`field${errors.email ? " has-error" : ""
                  }`}
              >
                <label htmlFor="regEmail">
                  Email
                </label>

                <input
                  type="email"
                  id="regEmail"
                  name="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  maxLength={LIMITS.email}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-invalid={!!errors.email}
                />

                {errors.email && (
                  <span className="field-error">
                    {errors.email}
                  </span>
                )}
              </div>

              <div
                className={`field${errors.referralCode ? " has-error" : ""
                  }`}
              >
                <label htmlFor="referralCode">
                  Referral Code
                </label>

                <input
                  type="text"
                  id="referralCode"
                  name="referralCode"
                  placeholder="Enter referral code"
                  maxLength={LIMITS.referralCode}
                  value={values.referralCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();

                    handleChange({
                      target: {
                        name: "referralCode",
                        type: "text",
                        value,
                      },
                    });

                    // Clear previous referral result
                    setReferralUser(null);
                    setReferralUserInvalid(false);
                  }}
                  onBlur={(e) => {
                    handleBlur(e);

                    fetchReferralUser(e.target.value);
                  }}
                  aria-invalid={!!errors.referralCode}
                  style={{
                    textTransform: "uppercase",
                  }}
                />

                {errors.referralCode && (
                  <span className="field-error">
                    {errors.referralCode}
                  </span>
                )}

                {/* Referral user loading */}
                {referralUserLoading && (
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "13px",
                      color: "#777",
                    }}
                  >
                    Checking referral ID...
                  </div>
                )}

                {/* Valid referral user */}
                {!referralUserLoading && referralUser && (
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "14px",
                      color: "#198754",
                      fontWeight: "500",
                    }}
                  >
                    Referral User: {referralUser.name}
                  </div>
                )}

                {/* Invalid referral */}
                {!referralUserLoading &&
                  !referralUser &&
                  referralUserInvalid && (
                    <div
                      style={{
                        marginTop: "6px",
                        fontSize: "14px",
                        color: "#dc3545",
                        fontWeight: "500",
                      }}
                    >
                      Invalid referral ID
                    </div>
                  )}


              </div>
              <div
                className={`field${errors.password ? " has-error" : ""
                  }`}
              >
                <label htmlFor="regPassword">
                  Password

                </label>

                <div
                  style={{
                    position: "relative",
                    width: "100%"
                  }}
                >
                  <input
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    id="regPassword"
                    name="password"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    maxLength={LIMITS.password}
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={!!errors.password}
                    style={{
                      paddingRight: 45
                    }}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        (prev) => !prev
                      )
                    }
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform:
                        "translateY(-50%)",
                      border: "none",
                      background: "transparent",
                      padding: 4,
                      margin: 0,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--text-dim)"
                    }}
                  >
                    {showPassword ? (
                      <FiEyeOff size={18} />
                    ) : (
                      <FiEye size={18} />
                    )}
                  </button>
                </div>

                {errors.password && (
                  <span className="field-error">
                    {errors.password}
                  </span>
                )}
              </div>




              <button
                type="submit"
                className="btn-global btn-primary-global form-submit"
                disabled={submitting}
              >
                {submitting
                  ? "Creating account..."
                  : "Create Account"}
              </button>

              {note && (
                <div
                  className={`form-note visible ${note.tone}`}
                >
                  {note.text}
                </div>
              )}
            </form>
            <div className="auth-topbar mt-3">
              <span></span>

              <span className="auth-switch">
                Already have an account?{" "}
                <Link to="/login">Log in</Link>
              </span>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>


  );

}
